import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Offer } from 'types/entities/Offer'
import { OffersAction } from 'store/offers/OffersAction'
import { useAppDispatch } from 'helpers/store'
import { useOpenSecondaryModal } from 'utils/hooks/useOpenSecondaryModal'
import { useRejectedReasons } from 'utils/hooks/useRejectedReasons'
import { OfferStatus } from 'types/enums/OfferStatus'
import { ReservationStatus } from 'types/enums/ReservationStatus'
import {
    getOfferDeletableReservationId,
    getOfferEditLockedTooltip,
    isOfferEditLocked,
} from 'utils/offers/offersUtils'
import { toast } from 'utils/toastUtils'
import { closeSecondaryModal, openSecondaryModal } from 'helpers/actions'
import UiAlertConfirmationModal from 'components/Ui/UiAlertConfirmationModal'
import { ModalSize } from 'types/enums/ModalSize'
import BookingLoginAction from 'store/bookingLogin/BookingLoginAction'
import EditOfferStatusModal from 'components/Offers/EditOfferStatusModal'
import EditLeadStatusModal from 'components/leads/EditLeadStatusModal'
import { useLinkedOpportunityForOffer } from 'utils/hooks/useLinkedOpportunityForOffer'
import {
    getLinkedOpportunityRejectionStatus,
    isLinkedOpportunityLead,
    isRejectedLinkedOpportunity,
} from 'utils/leads/linkedOpportunityRejectionUtils'
import ChangeOfferCompanyModal from 'components/Offers/ChangeOfferCompanyModal'
import ChangeOfferContactModal from 'components/Offers/ChangeOfferContactModal'
import ChangeOfferAddressModal from 'components/Offers/ChangeOfferAddressModal'
import EditOfferValidUntilModal from 'components/Offers/EditOfferValidUntilModal'
import EditOfferDateModal from 'components/Offers/EditOfferDateModal'
import { CompaniesAction } from 'store/companies/CompaniesAction'
import type { OfferRejectionReason } from 'types/entities/specific/OfferRejectionReason'

export const OFFER_DETAIL_EDIT_DISABLED_TOOLTIP_ID = 'offer-detail-edit-disabled-tooltip'

interface UseOfferDetailInfoActionsResult {
    acceptedEditTooltip: string
    hasConfirmedReservation: boolean
    isEditLocked: boolean
    isOfferRejected: boolean
    isOfferInReconfirm: boolean
    showOpportunityRejection: boolean
    rejectionComment: string
    rejectionOtherComment: string
    rejectionReasonLabels: { id: string; label: string }[]
    handleStatusChange: (newStatus: OfferStatus | null) => Promise<void>
    handleRejectionDetailsEdit: () => void
    handleProbabilityChange: (probability: number) => Promise<void>
    handleCompanyEdit: () => void
    handleContactEdit: () => void
    handleValidUntilEdit: () => void
    handleEventDateEdit: () => void
    handleAddressEdit: () => void
}

export const useOfferDetailInfoActions = (
    selectedOffer?: Offer
): UseOfferDetailInfoActionsResult => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const { openModal, closeModal } = useOpenSecondaryModal()
    const { reasons } = useRejectedReasons()
    const { linkedOpportunity, refreshLinkedOpportunity } = useLinkedOpportunityForOffer(selectedOffer)
    const hasLinkedOpportunity = isLinkedOpportunityLead(linkedOpportunity)
    const showOpportunityRejection = isRejectedLinkedOpportunity(linkedOpportunity)

    const hasConfirmedReservation =
        selectedOffer?.packagesCart?.reservation?.status === ReservationStatus.Confirmed
    const isOfferInReconfirm = selectedOffer?.status === OfferStatus.Reconfirm
    const isOfferRejected = selectedOffer?.status === OfferStatus.Rejected
    const isEditLocked = isOfferEditLocked(selectedOffer?.status)
    const acceptedEditTooltip = getOfferEditLockedTooltip(t, selectedOffer?.status)
    const linkedReservationId = selectedOffer
        ? getOfferDeletableReservationId(selectedOffer)
        : null

    const rejectionSource = showOpportunityRejection ? linkedOpportunity?.extra : selectedOffer?.extra

    const rejectionReasonLabels = useMemo(() => {
        const rejectionReasons = rejectionSource?.rejection_reasons ?? []
        return rejectionReasons.map((reasonId) => {
            const label =
                reasons.find((reason: OfferRejectionReason) => reason.id === reasonId)
                    ?.label || reasonId
            return { id: reasonId, label: t(label) }
        })
    }, [reasons, rejectionSource?.rejection_reasons, t])
    const rejectionOtherComment = rejectionSource?.rejection_other_comment?.trim() || ''
    const rejectionComment = rejectionSource?.rejection_comment?.trim() || ''

    const refreshOffer = () => {
        if (!selectedOffer) {
            return
        }

        return dispatch(OffersAction.fetchOfferById(selectedOffer.id))
    }

    const handleCompanyEdit = () => {
        if (!selectedOffer || isEditLocked) {
            return
        }

        openModal(
            <ChangeOfferCompanyModal
                offerId={selectedOffer.id}
                sellerId={selectedOffer.seller_id}
                initialCompany={selectedOffer.company}
                onSuccess={() => {
                    refreshOffer()
                }}
            />,
            {
                modalSize: ModalSize.Small,
                closeModal,
            },
            t('Change Company')
        )
    }

    const handleContactEdit = () => {
        if (!selectedOffer?.company || isEditLocked) {
            return
        }

        openModal(
            <ChangeOfferContactModal
                offerId={selectedOffer.id}
                sellerId={selectedOffer.seller_id}
                companyId={selectedOffer.company_id}
                addressId={selectedOffer.address_id}
                initialContact={selectedOffer.contact}
                companyContacts={selectedOffer.company?.contacts}
                onSuccess={() => {
                    refreshOffer()
                }}
            />,
            {
                modalSize: ModalSize.Small,
                closeModal,
            },
            t('Change Contact Person')
        )
    }

    const handleValidUntilEdit = () => {
        if (!selectedOffer || isEditLocked) {
            return
        }

        openModal(
            <EditOfferValidUntilModal
                offer={selectedOffer}
                onSuccess={() => {
                    // Offer is updated via Redux action
                }}
                isSecondary={true}
            />,
            {
                modalSize: ModalSize.Small,
                closeModal,
            },
            t('Edit Valid Until')
        )
    }

    const handleEventDateEdit = () => {
        if (!selectedOffer || isEditLocked) {
            return
        }

        openModal(
            <EditOfferDateModal
                offer={selectedOffer}
                onSuccess={() => {
                    refreshOffer()
                }}
                isSecondary={true}
            />,
            {
                modalSize: ModalSize.Small,
                closeModal,
            },
            t('Edit Event Date')
        )
    }

    const handleAddressEdit = () => {
        if (!selectedOffer?.company || isEditLocked) {
            return
        }

        openModal(
            <ChangeOfferAddressModal
                offerId={selectedOffer.id}
                sellerId={selectedOffer.seller_id}
                companyId={selectedOffer.company_id}
                contactId={selectedOffer.contact_id}
                initialAddressId={selectedOffer.address_id}
                onSuccess={() => {
                    refreshOffer()
                }}
            />,
            {
                modalSize: ModalSize.Small,
                closeModal,
            },
            t('Change Address')
        )
    }

    const handleProbabilityChange = async (probability: number) => {
        if (!selectedOffer || isEditLocked) {
            return
        }

        try {
            await dispatch(
                OffersAction.updateOffer({
                    id: selectedOffer.id,
                    seller_id: selectedOffer.seller_id,
                    probability,
                })
            )

            // Keep the current detail view responsive even when the update endpoint
            // returns a partial offer payload and the full refetch finishes later.
            dispatch(
                OffersAction.setSelectedOffer({
                    ...selectedOffer,
                    probability,
                })
            )

            void refreshOffer()
        } catch {
            toast.error(t('Failed to update probability'))
        }
    }

    const openOpportunityRejectionModal = () => {
        if (!linkedOpportunity) {
            return
        }

        openModal(
            <EditLeadStatusModal
                lead={linkedOpportunity}
                rejectionStatus={getLinkedOpportunityRejectionStatus(linkedOpportunity)}
                onSuccess={() => {
                    void refreshLinkedOpportunity()
                    void refreshOffer()
                }}
                isSecondary={true}
            />,
            {
                modalSize: ModalSize.Small,
                closeModal,
            },
            t('Rejection Details')
        )
    }

    const openOfferRejectionDetailsModal = () => {
        if (!selectedOffer) {
            return
        }

        const isEditingExistingRejection = selectedOffer.status === OfferStatus.Rejected

        openModal(
            <EditOfferStatusModal
                offer={selectedOffer}
                initialStatus={OfferStatus.Rejected}
                onSuccess={() => {
                    void refreshOffer()
                }}
                isSecondary={true}
            />,
            {
                modalSize: ModalSize.Small,
                closeModal,
            },
            isEditingExistingRejection ? t('Rejection Details') : t('Edit Offer Status')
        )
    }

    const openRejectionDetailsModal = () => {
        if (hasLinkedOpportunity) {
            openOpportunityRejectionModal()
            return
        }

        openOfferRejectionDetailsModal()
    }

    const handleRejectionDetailsEdit = () => {
        if (isOfferRejected) {
            return
        }
        openRejectionDetailsModal()
    }

    const handleStatusChange = async (newStatus: OfferStatus | null) => {
        if (!selectedOffer) {
            return
        }

        if (!newStatus) {
            return
        }

        if (isOfferRejected) {
            return
        }

        if (newStatus === selectedOffer.status) {
            if (newStatus === OfferStatus.Rejected) {
                openRejectionDetailsModal()
            }
            return
        }

        if (
            newStatus === OfferStatus.Accepted &&
            selectedOffer.status !== OfferStatus.Accepted
        ) {
            return
        }

        if (
            newStatus === OfferStatus.Reconfirm &&
            selectedOffer.status !== OfferStatus.Reconfirm &&
            !!linkedReservationId
        ) {
            dispatch(
                openSecondaryModal(
                    <UiAlertConfirmationModal
                        title={t('Reconfirm Offer')}
                        message={t(
                            'This offer already has a booking. Continuing will cancel the existing booking. After finishing your changes, click Create Booking again. A back-office change note will be created for admin@arbopark.ch and Lea Petersen.'
                        )}
                        confirmLabel={t('Continue') as string}
                        cancelLabel={t('Cancel') as string}
                        onConfirm={async () => {
                            dispatch(closeSecondaryModal())
                            try {
                                await dispatch(
                                    BookingLoginAction.fetchDeleteReservationOffer(
                                        linkedReservationId
                                    )
                                )

                                dispatch(OffersAction.clearSelectedOfferReservation())

                                await dispatch(
                                    OffersAction.updateOffer({
                                        id: selectedOffer.id,
                                        seller_id: selectedOffer.seller_id,
                                        status: newStatus,
                                    })
                                )

                                await dispatch(
                                    OffersAction.fetchAddOfferNote(
                                        selectedOffer.id,
                                        `RECONFIRM: Existing booking #${linkedReservationId} was cancelled before offer changes. Please inform admin@arbopark.ch and Lea Petersen. After offer updates, create a new booking from target calculation.`
                                    )
                                )

                                await refreshOffer()
                                toast.success(
                                    t(
                                        'Booking was cancelled and offer status was changed to Reconfirm. After your changes, click Create Booking again.'
                                    )
                                )
                            } catch (error) {
                                console.error(error)
                                toast.error(
                                    t(
                                        'Failed to switch offer to Reconfirm and cancel existing booking'
                                    )
                                )
                            }
                        }}
                        onCancel={() => dispatch(closeSecondaryModal())}
                    />,
                    {
                        modalSize: ModalSize.Small,
                        closeModal: () => dispatch(closeSecondaryModal()),
                    },
                    t('Confirm')
                )
            )
            return
        }

        if (newStatus === OfferStatus.Rejected) {
            if (hasLinkedOpportunity) {
                openOpportunityRejectionModal()
            } else {
                openOfferRejectionDetailsModal()
            }
            return
        }

        try {
            await dispatch(
                OffersAction.updateOffer({
                    id: selectedOffer.id,
                    seller_id: selectedOffer.seller_id,
                    status: newStatus,
                })
            )
            toast.success(t('Offer status updated'))
        } catch {
            toast.error(t('Failed to update status'))
        }
    }

    useEffect(() => {
        if (!selectedOffer) {
            return
        }

        if (selectedOffer.contact_id && !selectedOffer.contact) {
            dispatch(CompaniesAction.fetchCompanyContactById(selectedOffer.contact_id))
                .then((contact) => {
                    dispatch(
                        OffersAction.setSelectedOffer({
                            ...selectedOffer,
                            contact,
                        })
                    )
                })
                .catch(() => {})
        }
        if (selectedOffer.address_id && !selectedOffer.address) {
            dispatch(CompaniesAction.fetchCompanyAddressById(selectedOffer.address_id))
                .then((address) => {
                    dispatch(
                        OffersAction.setSelectedOffer({
                            ...selectedOffer,
                            address,
                        })
                    )
                })
                .catch(() => {})
        }
    }, [
        dispatch,
        selectedOffer,
        selectedOffer.contact_id,
        selectedOffer.address_id,
        selectedOffer.contact,
        selectedOffer.address,
    ])

    return {
        acceptedEditTooltip,
        hasConfirmedReservation,
        isEditLocked,
        isOfferRejected,
        isOfferInReconfirm,
        showOpportunityRejection,
        rejectionComment,
        rejectionOtherComment,
        rejectionReasonLabels,
        handleStatusChange,
        handleRejectionDetailsEdit,
        handleProbabilityChange,
        handleCompanyEdit,
        handleContactEdit,
        handleValidUntilEdit,
        handleEventDateEdit,
        handleAddressEdit,
    }
}
